import React, { useEffect, useState } from "react";
import { Modal } from "react-bootstrap";
import { RiCloseLargeFill } from "react-icons/ri";
import toast from "react-hot-toast";
import ImageTemplateServices from "../services/ImageTemplateServices";
import "../styles/AssignTemplateModal.css";

const templateApi = new ImageTemplateServices();

const AssignTemplateModal = ({ show, onClose, targetUser, onAssign, onSaved }) => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedId, setSelectedId] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!show) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const res = await templateApi.getActiveList();
        if (cancelled) return;
        if (res.isSuccess) {
          setTemplates(res.data || []);
          const current = targetUser?.template_id;
          const exists = (res.data || []).some(
            (t) => Number(t.id) === Number(current),
          );
          setSelectedId(exists ? String(current) : "");
        } else {
          toast.error(res.message || "Failed to load templates");
        }
      } catch (e) {
        if (!cancelled) toast.error("Failed to load templates");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [show, targetUser?.template_id]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!targetUser?.id) return;
    if (!selectedId) {
      toast.error("Select a template");
      return;
    }
    setSaving(true);
    try {
      const res = await onAssign({
        user_id: targetUser.id,
        template_id: Number(selectedId),
      });
      if (res?.isSuccess) {
        toast.success(res.message || "Template assigned");
        onSaved?.();
        onClose?.();
      } else {
        toast.error(res?.message || "Failed to assign template");
      }
    } catch (err) {
      toast.error(
        err?.response?.data?.message ||
          err?.message ||
          "Failed to assign template",
      );
    } finally {
      setSaving(false);
    }
  };

  const handleRemove = async () => {
    if (!targetUser?.id || !targetUser?.template_id) return;
    setSaving(true);
    try {
      const res = await onAssign({
        user_id: targetUser.id,
        template_id: null,
      });
      if (res?.isSuccess) {
        toast.success(res.message || "Template removed");
        onSaved?.();
        onClose?.();
      } else {
        toast.error(res?.message || "Failed to remove template");
      }
    } catch (err) {
      toast.error(
        err?.response?.data?.message ||
          err?.message ||
          "Failed to remove template",
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal show={show} onHide={onClose} centered backdrop="static" size="sm">
      <div className="atm-head">
        <h5 className="atm-title">Assign template</h5>
        <button
          type="button"
          className="atm-icon-close"
          onClick={onClose}
          aria-label="Close"
        >
          <RiCloseLargeFill size={18} />
        </button>
      </div>
      <Modal.Body className="atm-body">
        <div className="atm-user">
          <div className="atm-user-name">{targetUser?.name || "User"}</div>
          {targetUser?.username && (
            <div className="atm-user-sub">@{targetUser.username}</div>
          )}
        </div>
        <form id="assign-template-form" onSubmit={handleSubmit}>
          <label htmlFor="template_id" className="atm-label">
            Active template <span className="required-asterisk">*</span>
          </label>
          <select
            id="template_id"
            className="atm-select"
            value={selectedId}
            onChange={(e) => setSelectedId(e.target.value)}
            disabled={loading || saving}
          >
            <option value="">
              {loading ? "Loading…" : "-- Select template --"}
            </option>
            {templates.map((t) => (
              <option key={t.id} value={t.id}>
                {t.template_name}
              </option>
            ))}
          </select>
          {!loading && templates.length === 0 && (
            <div className="atm-empty">
              No active templates. Activate one in Image Template page.
            </div>
          )}
        </form>
      </Modal.Body>
      <Modal.Footer className="atm-footer">
        {targetUser?.template_id ? (
          <button
            type="button"
            className="atm-btn-danger"
            disabled={saving || loading}
            onClick={handleRemove}
          >
            Remove
          </button>
        ) : (
          <span />
        )}
        <div className="atm-footer-right">
          <button
            type="button"
            className="atm-btn-ghost"
            onClick={onClose}
            disabled={saving}
          >
            Cancel
          </button>
          <button
            type="submit"
            form="assign-template-form"
            className="atm-btn-primary"
            disabled={saving || loading || !selectedId}
          >
            {saving ? "Saving…" : "Assign"}
          </button>
        </div>
      </Modal.Footer>
    </Modal>
  );
};

export default AssignTemplateModal;
